import React, { ReactNode, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  useMediaQuery,
  Theme,
  styled,
  ButtonProps,
  useTheme,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Breadcrumb from "../../../Shared-ui/SharedComponents/Breadcrumb";
import variables from "../../../assets/styles/variables.module.scss";
import CreateOrganization from "../../Components/CreateOrganization";
// import CreateOrganization from "../../Components/CreateOrganization";
import { StartLoading, StopLoading } from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { useDispatch, useSelector } from "react-redux";
import { ShowError, ShowSuccess } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import { ADMIN_ORGANIZATIONS } from "../../../Services/Endpoint";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import ConfirmBase from "../../../Shared-ui/Types/Confirm";
import ConfirmPopup from "../../../Shared-ui/SharedComponents/ConfirmPopup";
import { Organization } from "../../../Shared-ui/Types/Admin";
import { formatDate } from "../../../Shared-ui/utils/Common/Common";

const ColorButton = styled(Button)<ButtonProps>(({ theme }) => ({
  color: variables.whiteColor,
  backgroundColor: variables.primaryButton,
  "&:hover": {
    backgroundColor: variables.primaryColor,
  },
}));


interface MyComponentProps { }

const AdminOrganization: React.FC<MyComponentProps> = () => {

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [organization, setOrganization] = useState<null | Organization>(null);
  const [organizations, setOrganizations] = useState([]);
  const dispatch = useDispatch();
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10 });
  const [organizationCount, setOrganizationCount] = useState(0)
  const [confirmPopupData, setConfirmPopupData] = useState<ConfirmBase>({ show: false })
  const prevPage = useRef<number>(pagination.page);
  const user = useSelector((state: any) => state.UserReducer);


  useEffect(() => {
    getOrganizations()
  }, [])

  useEffect(() => {
    if (prevPage.current !== pagination.page) {
      getOrganizations()
      prevPage.current = pagination.page;
    }
  }, [pagination])


  const getOrganizations = async () => {
    try {
      dispatch(StartLoading())
      const data = await BaseInstance.get(`${ADMIN_ORGANIZATIONS}?page=${pagination.page + 1}&page_size=${pagination.pageSize}`).then(res => res?.data)
      data?.items?.sort((a: { created_at: string }, b: { created_at: string }) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setOrganizationCount(data.pagination.total || 0)
      setOrganizations(data?.items || [])
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading())
    }
  }


  const deleteOrganizations = async (rowInfo: any) => {
    try {
      setConfirmPopupData((preVal) => ({ ...preVal, show: false }));
      dispatch(StartLoading())
      const organizations: Organization = rowInfo.row
      const data = await BaseInstance.delete(`${ADMIN_ORGANIZATIONS}/${organizations.id}`,).then(res => res?.data)
      dispatch(ShowSuccess(data?.message))
      getOrganizations()
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading())
    }
  }



  const handleEdit = (row: Organization) => {
    setOrganization(row)
    setOpen(true)
  };



  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "max_users",
      headerName: "Max Users",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "current_user_count",
      headerName: "Users Count",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "subscription_expires_at",
      headerName: "Subscription Expires",
      width: 150,
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <Box>
            {formatDate(params.value)}
          </Box>
        );
      },
    },
    {
      field: "created_at",
      headerName: "Created",
      width: 150,
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <Box>
            {formatDate(params.value)}
          </Box>
        );
      },
    },
    {
      field: "updated_at",
      headerName: "Updated",
      width: 150,
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <Box>{formatDate(params.value || params.row.created_at)}</Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const handleDeleteClick = (event: React.MouseEvent) => {
          setConfirmPopupData({
            show: true,
            setConfirmPopupData: setConfirmPopupData,
            title: "Confirm Deletion",
            info: "Are you sure want to delete your data? This action cannot be undone.",
            varient: "delete",
            functionName: deleteOrganizations,
            functionParam: params
          })
        };
        return (
          <Box sx={{ display: "flex", gap: 1, marginTop: 1.5, justifyContent: "center", alignItems: "center", width: "100%" }}>
            {user.role === "super admin" ? (
              <Tooltip title="Edit" placement="top-start">
                <IconButton
                  size="small"
                  onClick={() => handleEdit(params.row)}
                  sx={{ "&:hover": { color: "primary.main" } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {user.role === "super admin" ? (
              <Tooltip title="Delete" placement="top-start">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(params.row)}
                  sx={{ "&:hover": { color: "error.main" } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>)
      }

    },
  ];

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false)
    getOrganizations()
  };

  const handlePaginationMetaChange = (paginationMeta: any) => {
    const { page, pageSize, rowCount } = paginationMeta;
    setPagination({ page: page, pageSize: pageSize })
  };


  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { md: "flex-end", sm: "flex-start" },
            gap: { md: "10px", sm: "0" },
            flexDirection: { md: "row", sm: "column", xs: "column" },
          }}
        >
          <Breadcrumb data={[{ name: "Organization", path: "" }]} />
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          {user.role === "super admin" ? (
            <ColorButton
              variant="contained"
              disableElevation
              startIcon={<AddIcon />}
              size={isSmallScreen ? "small" : "large"}
              sx={{
                textTransform: "capitalize",
                fontWeight: "400",
                whiteSpace: "nowrap",
                borderRadius: { sm: "8px", xs: "4px" },
                height: "40px",
              }}
              onClick={handleOpen}
            >
              Add Organization
            </ColorButton>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
            height: "100%",
            gap: 4,
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Paper variant="outlined" sx={{ height: "100%", width: "100%" }}>
              <DataGrid
                rows={organizations}
                columns={columns}
                pageSizeOptions={[10]}
                rowCount={organizationCount}
                initialState={{ pagination: { paginationModel: pagination } }}
                onPaginationModelChange={handlePaginationMetaChange}
                disableColumnResize // Disables resizing
                paginationMode="server"
                disableRowSelectionOnClick
                sx={{
                  border: 0,
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                }}
              />
            </Paper>
          </Box>
        </Box>
      </Box>
      <CreateOrganization open={open} handleClose={handleClose} organization={organization} setOrganization={setOrganization} />
      <ConfirmPopup details={confirmPopupData} />
    </Container>
  );
};

export default AdminOrganization;
